import React from "react";
import PropTypes from "prop-types";
import { ArrowDown, ArrowUp, Info, Plus } from "lucide-react";
import { Tooltip } from "@mui/material";

import Breadcrumbs from "../../BreadCrumbs";
import { UIAvatar, UIButton } from "../../ui";
import { clx } from "../../../utils/Helpers";
import "./DashboardHeader.scss";

/**
 * Decorative discs bleeding off the hero edges (Figma). Values are px relative
 * to the hero box; the hero clips the overflow. Tweak size / offsets here.
 * `left` / `right` are treated as inline-start / inline-end so the layout
 * mirrors under RTL (Arabic) and the discs keep their place relative to content.
 * `bg` overrides the default faint fill (used for the bright dot by the avatar).
 */
const HERO_CIRCLES = [
  { id: "rightLarge", size: 220, top: -143, right: -107 },
  { id: "bottomLeft", size: 185, bottom: -104, left: -145 },
  { id: "bottomLeftSmall", size: 22, bottom: "20%", left: "15%" },
  { id: "topLeft", size: 280, top: -190, left: "12%" },
  { id: "topCenter", size: 103, top: -76, left: "50%" },
  { id: "topLeftSmall", size: 22, top: -6, left: "80%" },
  { id: "dot", size: 4, top: 70, left: 112, bg: "var(--color-foreground-inverse-30)" },
];

/**
 * Movement arrow per delta tone. Tones with no entry — "neutral" (period note)
 * and "muted" (empty-state message) — render their text without an arrow.
 */
const DELTA_ICONS = {
  positive: ArrowUp,
  negative: ArrowDown,
  pending: ArrowUp,
};

/**
 * Shared dashboard hero used by both the Company and Admin portals.
 * Presentational only — the composing page supplies the resolved user,
 * the period note, the stat tiles and the primary action.
 */
const DashboardHeader = ({
  breadcrumbLabel,
  userName,
  userImage,
  welcomeTemplate,
  welcomeFallback,
  subTitle,
  primaryAction,
  stats,
}) => {
  const welcome = userName
    ? welcomeTemplate.replace("{name}", userName)
    : welcomeFallback;

  return (
    <div className="dashboard-header">
      <div className="page-title-margin">
        <Breadcrumbs
          list={[{ path: "", label: breadcrumbLabel }]}
          BreadCrumbHasBackIcon={false}
        />
      </div>

      <section className="dashboard-header__hero">
        <div className="dashboard-header__decor" aria-hidden="true">
          {HERO_CIRCLES.map(({ id, size, top, left, right, bottom, bg }) => (
            <span
              key={id}
              className={clx(
                "dashboard-header__circle",
                `dashboard-header__circle--${id}`,
              )}
              style={{
                width: size,
                height: size,
                top,
                bottom,
                insetInlineStart: left,
                insetInlineEnd: right,
                background: bg,
              }}
            />
          ))}
        </div>

        <div className="dashboard-header__intro">
          <UIAvatar
            className="dashboard-header__avatar"
            image={userImage}
            name={userName || ""}
            size="lg"
          />
          <div className="dashboard-header__greeting">
            <h1 className="dashboard-header__title">
              {welcome}{" "}
              <span className="dashboard-header__wave" aria-hidden="true">
                👋
              </span>
            </h1>
            {subTitle && (
              <p className="dashboard-header__period">{subTitle}</p>
            ) }
          </div>

          {primaryAction && (
            <UIButton
              appearance="gradient"
              startIcon={<Plus size={18} strokeWidth={2} />}
              className="dashboard-header__action"
              onClick={primaryAction.onClick}
            >
              {primaryAction.label}
            </UIButton>
          )}
        </div>

        {stats.length > 0 && (
          <ul className="dashboard-header__stats">
            {stats.map((stat) => {
              const DeltaIcon = DELTA_ICONS[stat.deltaTone];

              return (
                <li key={stat.id} className="dashboard-header__stat">
                  <span className="dashboard-header__stat-label">
                    {stat.label}
                    {stat.labelNote ? (
                      <span className="dashboard-header__stat-label-note">
                        {" "}
                        {stat.labelNote}
                      </span>
                    ) : null}
                    {stat.info ? (
                      <Tooltip title={stat.info} placement="top" arrow>
                        <span
                          className="dashboard-header__stat-info"
                          tabIndex={0}
                          aria-label={stat.info}
                        >
                          <Info size={13} strokeWidth={2} aria-hidden="true" />
                        </span>
                      </Tooltip>
                    ) : null}
                  </span>

                  {/* value + delta bottom-align so they line up across tiles
                      no matter how many lines the label wraps to */}
                  <span className="dashboard-header__stat-figure">
                    <span className="dashboard-header__stat-value">
                      {stat.value}
                      {stat.valueSuffix ? (
                        <span className="dashboard-header__stat-value-suffix">
                          {stat.valueSuffix}
                        </span>
                      ) : null}
                    </span>
                    {stat.delta && (
                      <span
                        className={clx(
                          "dashboard-header__stat-delta",
                          stat.deltaTone &&
                            `dashboard-header__stat-delta--${stat.deltaTone}`,
                        )}
                      >
                        {DeltaIcon && <DeltaIcon size={12} strokeWidth={2.5} />}
                        {stat.delta}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

DashboardHeader.propTypes = {
  breadcrumbLabel: PropTypes.string.isRequired,
  userName: PropTypes.string,
  userImage: PropTypes.string,
  welcomeTemplate: PropTypes.string.isRequired,
  welcomeFallback: PropTypes.string.isRequired,
  subTitle: PropTypes.string,
  primaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      labelNote: PropTypes.string,
      // Tooltip text for an info icon beside the label (what the number counts).
      info: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      // Kept out of `value` (its own span) so it doesn't get pulled into RTL
      // bidi reordering with the number, e.g. "%" after a live percentage.
      valueSuffix: PropTypes.string,
      delta: PropTypes.string,
      deltaTone: PropTypes.oneOf([
        "positive",
        "negative",
        "pending",
        "neutral",
        "muted",
      ]),
    }),
  ),
};

DashboardHeader.defaultProps = {
  userName: "",
  userImage: undefined,
  subTitle: "",
  primaryAction: null,
  stats: [],
};

export default DashboardHeader;
